/**
 * GuidedTour - Interactive App Walkthrough
 * 
 * Timo guides users through the actual app interface,
 * highlighting real UI elements and explaining features.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Globe, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { playTTS } from '@/utils/tts';

// Import Timo images
import TimoAiImage from '@/assets/TimoAi.webp';

const TOUR_STORAGE_KEY = 'ttt_guided_tour_completed';

// Tour step configuration with target selectors and positions
const getTourSteps = (t) => [
  {
    id: 'welcome',
    route: '/',
    target: null, // No target, just Timo talking
    position: 'center',
    title: t('tour.welcome.title'),
    message: t('tour.welcome.message'),
  },
  {
    id: 'navbar',
    route: '/',
    target: '[data-tour="bottom-nav"]',
    position: 'top',
    title: t('tour.navbar.title'),
    message: t('tour.navbar.message'),
    highlightPadding: 8,
  },
  {
    id: 'timo-button',
    route: '/',
    target: '[data-tour="timo-button"]',
    position: 'top',
    title: t('tour.timoButton.title'),
    message: t('tour.timoButton.message'),
    highlightPadding: 12,
  },
  {
    id: 'home-tab',
    route: '/',
    target: '[data-tour="nav-home"]',
    position: 'top',
    title: t('tour.homeTab.title'),
    message: t('tour.homeTab.message'),
    highlightPadding: 8,
  },
  {
    id: 'scripts-tab',
    route: '/',
    target: '[data-tour="nav-scripts"]',
    position: 'top',
    title: t('tour.scriptsTab.title'),
    message: t('tour.scriptsTab.message'),
    highlightPadding: 8,
  },
  {
    id: 'profile-tab',
    route: '/',
    target: '[data-tour="nav-profile"]',
    position: 'top',
    title: t('tour.profileTab.title'),
    message: t('tour.profileTab.message'),
    highlightPadding: 8,
  },
  {
    id: 'complete',
    route: '/',
    target: null,
    position: 'center',
    title: t('tour.complete.title'),
    message: t('tour.complete.message'),
  },
];

// Calculate position for the spotlight and tooltip
const getTargetRect = (selector) => {
  if (!selector) return null;
  const element = document.querySelector(selector);
  if (!element) return null;
  return element.getBoundingClientRect();
};

// Spotlight overlay that darkens everything except the target
const SpotlightOverlay = ({ targetRect, padding = 0, targetSelector }) => {
  // Elevate target element when it's being highlighted
  useEffect(() => {
    if (targetSelector) {
      const element = document.querySelector(targetSelector);
      if (element) {
        // Only change z-index, don't touch position (it might be fixed)
        const originalZIndex = element.style.zIndex;
        element.style.zIndex = '93';
        return () => {
          element.style.zIndex = originalZIndex;
        };
      }
    }
  }, [targetSelector]);

  // When no target, just darken the screen
  if (!targetRect) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[90]" />
    );
  }

  const { top, left, width, height } = targetRect;
  const paddedTop = top - padding;
  const paddedLeft = left - padding;
  const paddedWidth = width + padding * 2;
  const paddedHeight = height + padding * 2;

  // Use 4 divs to create the dark overlay around the spotlight
  return (
    <>
      {/* Top overlay */}
      <div 
        className="fixed left-0 right-0 top-0 bg-black/60 z-[90]"
        style={{ height: paddedTop }}
      />
      {/* Bottom overlay */}
      <div 
        className="fixed left-0 right-0 bottom-0 bg-black/60 z-[90]"
        style={{ top: paddedTop + paddedHeight }}
      />
      {/* Left overlay */}
      <div 
        className="fixed left-0 bg-black/60 z-[90]"
        style={{ 
          top: paddedTop, 
          width: paddedLeft, 
          height: paddedHeight 
        }}
      />
      {/* Right overlay */}
      <div 
        className="fixed right-0 bg-black/60 z-[90]"
        style={{ 
          top: paddedTop, 
          left: paddedLeft + paddedWidth,
          height: paddedHeight 
        }}
      />
      
      {/* Highlight border around target */}
      <div
        className="fixed rounded-2xl z-[91] pointer-events-none"
        style={{
          top: paddedTop,
          left: paddedLeft,
          width: paddedWidth,
          height: paddedHeight,
          border: '3px solid #9DDBB9',
          boxShadow: '0 0 0 4px rgba(157, 219, 185, 0.4), inset 0 0 0 1px rgba(157, 219, 185, 0.2)',
        }}
      />
    </>
  );
};

// Timo with speech bubble
const TimoGuide = ({ title, message, position, targetRect, onNext, onBack, onSkip, isFirst, isLast, t, language, setLanguage, lightMode }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Play TTS for the message
  const handleTTS = async () => {
    if (isSpeaking) return;
    setIsSpeaking(true);
    try {
      await playTTS(message, language, 'elevenlabs');
    } catch (e) {
      console.error('TTS error:', e);
    }
    // Reset after approximate speech duration
    setTimeout(() => setIsSpeaking(false), 2000);
  };
  
  // Calculate position based on target - always center horizontally with safe margins
  let style = {
    left: '16px',
    right: '16px',
    width: 'auto',
    maxWidth: '340px',
    margin: '0 auto',
  };
  
  if (position === 'center' || !targetRect) {
    style = {
      ...style,
      top: '50%',
      transform: 'translateY(-50%)',
    };
  } else if (position === 'top') {
    // Position above the target (for bottom nav items)
    const topPosition = targetRect.top - 12;
    style = {
      ...style,
      bottom: `${window.innerHeight - topPosition}px`,
    };
  } else if (position === 'bottom') {
    style = {
      ...style,
      top: `${targetRect.bottom + 12}px`,
    };
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed z-[95]"
      style={style}
    >
      {/* Speech bubble - theme-aware card */}
      <div className={cn(
        "rounded-2xl p-4 shadow-2xl w-full border",
        lightMode 
          ? "bg-white border-gray-200" 
          : "bg-[#1a1a2e] border-white/10"
      )}>
        {/* Header with Timo and title */}
        <div className="flex items-center gap-3 mb-2">
          {/* Timo avatar */}
          <img
            src={TimoAiImage}
            alt="Timo"
            className="w-12 h-12 object-contain flex-shrink-0"
            draggable="false"
          />
          
          <div className="flex-1">
            <h3 className={cn(
              "font-bold text-base",
              lightMode ? "text-gray-900" : "text-white"
            )}>
              {title}
            </h3>
          </div>
          
          {/* TTS button */}
          <button
            onClick={handleTTS}
            className={cn(
              "p-2 rounded-lg transition-colors",
              isSpeaking 
                ? "bg-[#9DDBB9] text-[#1a1a2e]" 
                : lightMode
                  ? "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
            )}
            aria-label="Read aloud"
          >
            <Volume2 className="w-4 h-4" />
          </button>
          
          {/* Language toggle */}
          <button
            onClick={() => setLanguage(language === 'en' ? 'lt' : 'en')}
            className={cn(
              "flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors",
              lightMode
                ? "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                : "bg-white/10 hover:bg-white/20 text-white/70 hover:text-white"
            )}
          >
            <Globe className="w-3 h-3" />
            {language === 'en' ? 'LT' : 'EN'}
          </button>
        </div>
        
        {/* Message */}
        <p className={cn(
          "text-sm leading-relaxed mb-3 pl-1",
          lightMode ? "text-gray-700" : "text-white/80"
        )}>
          {message}
        </p>

        {/* Navigation buttons */}
        <div className={cn(
          "flex items-center gap-2 pt-3 border-t",
          lightMode ? "border-gray-200" : "border-white/10"
        )}>
          {!isFirst && (
            <button
              onClick={onBack}
              className={cn(
                "flex items-center gap-1 px-3 py-2 text-sm transition-colors rounded-lg",
                lightMode
                  ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              )}
            >
              <ChevronLeft className="w-4 h-4" />
              {t('tour.back')}
            </button>
          )}
          
          <div className="flex-1" />
          
          {!isLast && (
            <button
              onClick={onSkip}
              className={cn(
                "px-3 py-2 text-sm transition-colors",
                lightMode
                  ? "text-gray-400 hover:text-gray-600"
                  : "text-white/40 hover:text-white/70"
              )}
            >
              {t('tour.skip')}
            </button>
          )}
          
          <button
            onClick={onNext}
            className="flex items-center gap-1 px-4 py-2.5 bg-[#9DDBB9] hover:bg-[#7ECC9F]
                      text-[#1a1a2e] font-bold text-sm rounded-xl transition-colors shadow-md"
          >
            {isLast ? t('tour.finish') : t('tour.next')}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main GuidedTour Component
const GuidedTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { lightMode } = useAccessibility();
  
  // Regenerate steps when language changes
  const TOUR_STEPS = useMemo(() => getTourSteps(t), [language, t]);
  const step = TOUR_STEPS[currentStep];

  // Lock body scroll when tour is active
  useEffect(() => {
    // Simple overflow hidden - doesn't affect layout
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Update target rect when step changes or window resizes
  const updateTargetRect = useCallback(() => {
    if (step?.target) {
      const rect = getTargetRect(step.target);
      console.log('Target rect for', step.target, rect); // Debug
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    // Navigate to the correct route for this step
    if (step?.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    
    // Small delay to let the page render before finding targets
    const timer = setTimeout(updateTargetRect, 150);
    
    window.addEventListener('resize', updateTargetRect);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [step, location.pathname, navigate, updateTargetRect]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    onComplete?.();
  };

  // Block clicks on the overlay except for navigation buttons
  const handleOverlayClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div 
      className="fixed inset-0 z-[89] overflow-hidden" 
      onClick={handleOverlayClick}
      style={{ touchAction: 'none' }}
    >
      {/* Spotlight overlay */}
      <SpotlightOverlay 
        targetRect={targetRect} 
        padding={step?.highlightPadding || 0}
        targetSelector={step?.target}
      />
      
      {/* Timo guide */}
      <AnimatePresence mode="wait">
        <TimoGuide
          key={step.id}
          title={step.title}
          message={step.message}
          position={step.position}
          targetRect={targetRect}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={handleSkip}
          isFirst={currentStep === 0}
          isLast={currentStep === TOUR_STEPS.length - 1}
          t={t}
          language={language}
          setLanguage={setLanguage}
          lightMode={lightMode}
        />
      </AnimatePresence>
    </div>
  );
};

// Hook to manage tour state
export const useGuidedTour = () => {
  const [showTour, setShowTour] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    // HIDDEN: Tutorial is now disabled
    // if (!completed) {
    //   setShowTour(true);
    // }
    setShowTour(false); // Always hide tutorial
    setHasChecked(true);
  }, []);

  // Listen for replay event from Settings
  useEffect(() => {
    const handleReplayTour = () => {
      localStorage.removeItem(TOUR_STORAGE_KEY);
      setShowTour(true);
    };
    
    window.addEventListener('replay-guided-tour', handleReplayTour);
    return () => window.removeEventListener('replay-guided-tour', handleReplayTour);
  }, []);

  const completeTour = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setShowTour(false);
  };

  const resetTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setShowTour(true);
  };

  const startTour = () => {
    setShowTour(true);
  };

  return { showTour, hasChecked, completeTour, resetTour, startTour };
};

export default GuidedTour;
